import React, { useState } from 'react';
import { Box, Button, Chip, Divider, LinearProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000').replace(/\/api\/?$/, '');

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908C16.658 14.121 17.64 11.813 17.64 9.2z" fill="#4285F4" />
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853" />
    <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
  </svg>
);

const getPasswordStrength = (pw: string): { score: number; label: string; color: 'error' | 'warning' | 'success' } => {
  if (pw.length === 0) return { score: 0, label: '', color: 'error' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { score: 25, label: 'Weak', color: 'error' };
  if (score === 2) return { score: 50, label: 'Fair', color: 'warning' };
  if (score === 3) return { score: 75, label: 'Good', color: 'success' };
  return { score: 100, label: 'Strong', color: 'success' };
};

const Signup: React.FC = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const referralCode = new URLSearchParams(location.search).get('ref') || undefined;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await register(name, email, password, referralCode);
      navigate('/onboarding');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Unable to create account.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: 'calc(100vh - 260px)', display: 'grid', placeItems: 'center', px: 2, py: { xs: 5, md: 7 }, bgcolor: '#f5f7fb' }}>
      <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, boxShadow: '0 20px 50px rgba(7,24,45,0.08)', width: '100%', maxWidth: 480, borderColor: 'rgba(7,24,45,0.08)' }}>
        <Stack spacing={2} component="form" onSubmit={handleSubmit}>
          <Typography variant="h5" fontWeight={700}>Create your RideSaathi account</Typography>
          <Typography variant="body2" color="text.secondary">
            Unlock rides free every month and upgrade anytime.
          </Typography>
          {referralCode && (
            <Chip
              label={`Referred by a driver · You'll both earn bonus unlocks`}
              color="success"
              variant="outlined"
              size="small"
            />
          )}

          <Button
            variant="outlined"
            size="large"
            fullWidth
            startIcon={<GoogleIcon />}
            onClick={() => { window.location.href = `${BACKEND_URL}/api/auth/google`; }}
            sx={{ textTransform: 'none', borderColor: 'rgba(0,0,0,0.23)', color: 'text.primary', justifyContent: 'center', gap: 1 }}
          >
            Sign up with Google
          </Button>

          <Divider>or sign up with email</Divider>

          <TextField
            label="Full name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            fullWidth
          />
          <Box>
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              fullWidth
            />
            {password.length > 0 && (() => {
              const { score, label, color } = getPasswordStrength(password);
              return (
                <Box mt={0.75}>
                  <LinearProgress
                    variant="determinate"
                    value={score}
                    color={color}
                    sx={{ height: 4, borderRadius: 2 }}
                  />
                  <Typography variant="caption" color={`${color}.main`} mt={0.25} display="block">
                    {label} password
                  </Typography>
                </Box>
              );
            })()}
          </Box>
          {error && (
            <Typography variant="body2" color="error">{error}</Typography>
          )}
          <Button type="submit" variant="contained" color="secondary" size="large" disabled={loading}>
            {loading ? 'Creating…' : 'Create Account'}
          </Button>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Button component={RouterLink} to="/login" size="small">Sign in</Button>
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Signup;
